import React, { useState } from 'react';
import {
  Container,
  Button,
  Tooltip,
  OverlayTrigger,
  ButtonGroup,
} from 'react-bootstrap';
import './pricing.css';

// Define types for pricing data
type TierFeature = {
  text: string;
  info?: string; // Optional tooltip text
  strikethrough?: boolean; // For crossed out features
  note?: string; // Additional note for features
};

type PricingTier = {
  icon: string; // Bootstrap icon class name (e.g., 'bi-cup')
  title: string;
  monthlyPrice: number | null; // Store as number for calculation
  yearlyPrice?: number | null; // Will be calculated
  audience: string;
  features: TierFeature[];
  previousTier?: string; // Name of the tier below (for "Everything in...")
  monthlyTrialDays: number;
  yearlyTrialDays: number;
  ctaText: string;
  ctaVariant: 'primary' | 'outline-primary' | 'secondary';
  ctaAction?: () => void; // Optional custom action
  contactSales?: boolean; // Flag for Enterprise
};

// --- Helper Function to Apply Discount ---
const applyDiscount = (
  price: number | null,
  discountFactor: number,
): number | null => {
  if (price === null || price === 0) {
    return price;
  }
  // Calculate discounted price and round to two decimal places
  const discounted = price * discountFactor;
  return Math.round(discounted * 100) / 100;
};

// Base features for Free tier
const freeFeatures: TierFeature[] = [
  {
    text: 'AI Report Generation (5/month)',
    info: 'Generate up to 5 AI-assisted reports per month.',
  },
  {
    text: 'Voice-to-AI-Polished Text',
    info: 'Comparing to Google Voice-to-Text, our specialized AI recognizes home inspection terminologies.',
  },
  { text: '1-Click Report Export' },
  {
    text: 'Customer Support',
    info: 'Submit bug reports and feedback forms to the development team.',
  },
  {
    text: 'Self-Serve Onboarding',
    info: 'Access documentation and resources for do-it-yourself setup.',
  },
];

// New features added in Basic (relative to Free)
const basicNewFeatures: TierFeature[] = [
  {
    text: 'AI Report Generation (Unlimited)',
    info: 'Remove the monthly limit on AI-generated reports.',
  },
  {
    text: 'Smart Template Import',
    info: 'Import and use competitor templates for report sections.',
  },
  {
    text: 'Personalized AI Style',
    info: 'AI adapts to your writing style based on previous templates and reports.',
  },
  {
    text: 'Email and Chat Support',
    info: 'Direct access to support via email and live chat.',
  },
  {
    text: 'Scheduled Onboarding',
    info: 'Book a time with our onboarding team to get your account set up.',
  },
];

// New features added in Team (relative to Solo)
const proNewFeatures: TierFeature[] = [
  {
    text: 'Team Collaboration',
    info: 'Invite your team to collaborate on running your business.',
  },
  {
    text: 'CRM Integration',
    info: 'Schedule inspections and manage clients.',
  },
  {
    text: 'AI Automation',
    info: 'Automate email, SMS, and reminders to both you and your clients.',
  },
  {
    text: 'Contract and Payment Management',
    info: 'Manage contracts and payments for your clients.',
  },
  {
    text: 'Team-Shared Templates',
    info: 'Create, import, and share templates across your team.',
  },
  {
    text: 'Team AI Style',
    info: "AI learns and adapts to your team's collective writing style.",
  },
  { text: 'Multi-Inspector Support' },
  {
    text: 'Faster Response Support',
    info: 'Priority email, chat, and phone support channels.',
  },
];

// New features added in Enterprise (relative to Pro)
const enterpriseNewFeatures: TierFeature[] = [
  {
    text: 'Advanced CRM Integration',
    info: 'In-depth business insights and analytics.',
  },
  {
    text: 'Advanced AI Automation',
    info: 'Customized AI automation workflowswith 1,000+ apps for your business.',
  },
  { text: 'Customized Report Styles' },
  {
    text: 'Advanced Template Analysis',
    info: 'AI-driven recommendations and optimizations for imported templates.',
  },
  {
    text: 'Enterprise AI Training',
    info: 'Custom AI models finetuned on your organizational data and style guides.',
  },
  {
    text: 'Advanced Team Collaboration',
    info: 'Granular role management and multi-department support.',
  },
  { text: 'Full API Access' },
  {
    text: 'Dedicated Account Manager',
    info: 'High-touch support with a single point of contact for all needs.',
  },
  { text: 'In-person Setup & Training' },
];

// --- Updated Pricing Data ---
const getPricingTiersData = (): Omit<PricingTier, 'yearlyPrice'>[] => [
  {
    icon: 'bi-cup-straw',
    title: 'Free',
    monthlyPrice: 0,
    audience: 'Ideal for casual inspectors exploring the platform.',
    features: freeFeatures,
    monthlyTrialDays: 0, // No trial usually for free
    yearlyTrialDays: 0,
    ctaText: 'Get Started',
    ctaVariant: 'primary',
    ctaAction: () => {
      const element = document.getElementById('download');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
  {
    icon: 'bi-person',
    title: 'Solo',
    monthlyPrice: 29,
    audience: 'Perfect for solo inspectors managing their workload.',
    features: basicNewFeatures,
    previousTier: 'Free',
    monthlyTrialDays: 7,
    yearlyTrialDays: 30,
    ctaText: 'Get Started',
    ctaVariant: 'primary',
    ctaAction: () => {
      const element = document.getElementById('download');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
  {
    icon: 'bi-people',
    title: 'Team',
    monthlyPrice: 49,
    audience: 'Designed for inspection teams needing collaboration tools.',
    features: proNewFeatures,
    previousTier: 'Solo',
    monthlyTrialDays: 7,
    yearlyTrialDays: 30,
    ctaText: "Coming Soon in Q1'26",
    ctaVariant: 'secondary',
  },
  {
    // icon for an enterprise
    icon: 'bi-building',
    title: 'Enterprise',
    monthlyPrice: null, // Contact sales
    audience:
      'Comprehensive solution for companies requiring advanced integrations and support.',
    features: enterpriseNewFeatures,
    previousTier: 'Team',
    monthlyTrialDays: 0, // Usually demo/consultation
    yearlyTrialDays: 0,
    ctaText: 'Coming Soon',
    ctaVariant: 'secondary',
    contactSales: true,
  },
];

// --- Pricing Card Component ---
const PricingCard = ({
  tier,
  isYearly,
}: {
  tier: PricingTier;
  isYearly: boolean;
}) => {
  const {
    icon,
    title,
    monthlyPrice,
    audience,
    features,
    previousTier,
    ctaText,
    ctaVariant,
    ctaAction,
    contactSales,
    monthlyTrialDays,
    yearlyTrialDays,
  } = tier;

  // Calculate effective monthly display price based on toggle
  const displayPrice = isYearly
    ? typeof tier.yearlyPrice === 'number' // Use tier.yearlyPrice directly
      ? tier.yearlyPrice > 0
        ? Math.round((tier.yearlyPrice / 12) * 100) / 100 // Calculate and round effective monthly price
        : 0
      : null
    : tier.monthlyPrice; // Use monthly price (already rounded by applyDiscount)

  // Define the period text based on tier title (always per month)
  const displayPeriod = title === 'Team' ? 'month / seat' : 'month';

  // Other display flags
  const displayTrialDays = isYearly ? yearlyTrialDays : monthlyTrialDays;
  const showSavings =
    isYearly &&
    typeof tier.yearlyPrice === 'number' && // Ensure yearlyPrice is a number
    typeof monthlyPrice === 'number' && // Ensure monthlyPrice is a number
    tier.yearlyPrice < monthlyPrice * 12; // Show savings if yearly is cheaper than 12 * monthly
  const showTrial = displayTrialDays > 0 && !contactSales;
  const emphasizeTrial = isYearly && yearlyTrialDays > monthlyTrialDays;

  // Helper to format price: show one decimal or integer if .0
  const formatPrice = (price: number | null): string => {
    if (price === null) return 'N/A';
    const formatted = price.toFixed(2);
    return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
  };

  const handleCTAClick = () => {
    if (ctaAction) {
      ctaAction();
    }
  };

  return (
    <div className="pricing-card">
      <div className="card-header">
        <i className={`card-icon ${icon}`}></i>
        <h3 className="card-title">{title}</h3>
      </div>
      <div className="card-price">
        {displayPrice !== null ? ( // Check if there's a price to display
          <>
            {/* Display price, ensuring .0 if needed */}
            <span className="price-amount">${formatPrice(displayPrice)}</span>
            {/* Display period text (e.g., / month / seat) */}
            <span className="price-period">/ {displayPeriod}</span>
            {/* Conditionally render total annual price and "billed annually" on a new line */}
            {isYearly && typeof tier.yearlyPrice === 'number' && (
              <span
                className="billing-frequency"
                style={{
                  display: 'block',
                  fontSize: '0.8em',
                  color: '#6c757d',
                  marginTop: '-3px',
                }}
              >
                ${formatPrice(tier.yearlyPrice)} billed annually
              </span>
            )}
            {showSavings && <span className="savings-badge">Save ~17%</span>}
          </>
        ) : (
          <span className="price-contact">Contact Us</span> // Display 'Contact Us' if price is null
        )}
        <p className={`trial-info ${emphasizeTrial ? 'emphasized' : ''}`}>
          {
            showTrial
              ? `${displayTrialDays}-day free trial`
              : '\u00A0' /* Non-breaking space */
          }
        </p>
      </div>
      <p className="card-audience">{audience}</p>
      <ul className="card-features">
        {previousTier && (
          <li className="feature-item includes-previous">
            <i className="bi bi-check feature-check"></i>
            <span>Everything in {previousTier}, plus:</span>
          </li>
        )}
        {features.map((feature, index) => (
          <li key={index} className="feature-item">
            <i className="bi bi-check feature-check"></i>
            {feature.info ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-${tier.title}-${index}`}>
                    {feature.info}
                  </Tooltip>
                }
              >
                <span className="feature-text-with-info">
                  <span
                    className={feature.strikethrough ? 'strikethrough' : ''}
                  >
                    {feature.text}
                  </span>
                  {feature.note && (
                    <span className="feature-note">{feature.note}</span>
                  )}
                </span>
              </OverlayTrigger>
            ) : (
              <span>
                <span className={feature.strikethrough ? 'strikethrough' : ''}>
                  {feature.text}
                </span>
                {feature.note && (
                  <span className="feature-note">{feature.note}</span>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="card-footer">
        <Button
          variant={ctaVariant}
          className="cta-btn"
          onClick={handleCTAClick}
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};

// --- Main Pricing Component ---
const Pricing = ({
  discountFactor = 1,
  discountMessage,
}: {
  discountFactor?: number;
  discountMessage?: string;
}) => {
  const [isYearly, setIsYearly] = useState(true);

  // Calculate yearly prices and apply discount
  const pricingTiers: PricingTier[] = getPricingTiersData().map((tier) => {
    const discountedMonthly = applyDiscount(tier.monthlyPrice, discountFactor);
    return {
      ...tier,
      monthlyPrice: discountedMonthly, // Store discounted monthly price
      yearlyPrice: discountedMonthly !== null ? discountedMonthly * 10 : null, // Calculate discounted yearly price
    };
  });

  return (
    <section className="pricing" id="pricing">
      <Container fluid="xl">
        <h2 className="pricing-title">
          Choose the Plan That&apos;s Right for You
        </h2>
        <p className="pricing-subtitle">
          Simple, transparent pricing for teams of all sizes.
        </p>

        {/* Display Discount Message if provided */}
        {discountMessage && (
          <div className="discount-message alert alert-success text-center">
            {discountMessage}
          </div>
        )}

        {/* --- Pill Toggle Buttons --- */}
        <div className="billing-toggle-container">
          <ButtonGroup className="billing-toggle-pill">
            <Button
              variant={
                !isYearly ? 'primary-active-pill' : 'secondary-inactive-pill'
              }
              onClick={() => setIsYearly(false)}
              className="toggle-button monthly"
            >
              Monthly
            </Button>
            <Button
              variant={
                isYearly ? 'primary-active-pill' : 'secondary-inactive-pill'
              }
              onClick={() => setIsYearly(true)}
              className="toggle-button yearly"
            >
              Yearly
            </Button>
          </ButtonGroup>
        </div>

        <div className="pricing-cards-wrapper">
          {pricingTiers.map((tier) => (
            <PricingCard key={tier.title} tier={tier} isYearly={isYearly} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Pricing;
